import { useEffect, type Dispatch, type MouseEvent, type MutableRefObject, type RefObject, type SetStateAction } from "react"
import type { ParsedQuery } from "@/lib/search/query-parser"
import type { CommandItem } from "./command-palette.types"
import { queryOptions } from "./command-palette.utils"

export interface UseCommandPaletteKeyboardParams {
  open: boolean
  inputRef: RefObject<HTMLInputElement | null>
  search: string
  trimmedSearch: string
  selectedIndex: number
  setSelectedIndex: Dispatch<SetStateAction<number>>
  setSearch: Dispatch<SetStateAction<string>>
  displayResults: CommandItem[]
  isUrl: boolean
  /** When true, hide the generic Open URL row (Maater document paste). */
  isMaaterDocUrl?: boolean
  /** When true, hide the generic Open URL row (Google Drive/Docs paste). */
  isDriveUrl?: boolean
  isId: boolean
  showQueryOptions: boolean
  searchHistory: string[]
  historyIndex: number
  setHistoryIndex: Dispatch<SetStateAction<number>>
  currentInput: string
  setCurrentInput: Dispatch<SetStateAction<string>>
  parsedQuery: ParsedQuery
  currentChatTarget: "current" | "new"
  setCurrentChatTarget: Dispatch<SetStateAction<"current" | "new">>
  setOpen: (open: boolean) => void
  openChatWithPrompt: (query: string, opts?: { newThread?: boolean }) => void
  handleSelectRef: MutableRefObject<(item: CommandItem, event?: MouseEvent) => void>
  handleSpecialSearchRef: MutableRefObject<() => void>
}

/** Keyboard navigation, search history, and Enter/Tab actions for the command palette. */
export function useCommandPaletteKeyboard({
  open,
  inputRef,
  search,
  trimmedSearch,
  selectedIndex,
  setSelectedIndex,
  setSearch,
  displayResults,
  isUrl,
  isMaaterDocUrl = false,
  isDriveUrl = false,
  isId,
  showQueryOptions,
  searchHistory,
  historyIndex,
  setHistoryIndex,
  currentInput,
  setCurrentInput,
  parsedQuery,
  currentChatTarget,
  setCurrentChatTarget,
  setOpen,
  openChatWithPrompt,
  handleSelectRef,
  handleSpecialSearchRef,
}: UseCommandPaletteKeyboardParams) {
  useEffect(() => {
    if (!open) return

    const handleKeyDown = (e: KeyboardEvent) => {
      // Radix menus (workspace scope dropdown) portal outside the dialog and own their arrow/Enter keys.
      if (e.target instanceof Element && e.target.closest('[role="menu"]')) return
      const input = inputRef.current
      let isInputFocused = document.activeElement === input
      const cursorAtStart = input?.selectionStart === 0 && input?.selectionEnd === 0
      const inputEmpty = search.length === 0

      const showExternalUrlRow = isUrl && !isMaaterDocUrl && !isDriveUrl
      const totalItems =
        (showQueryOptions ? queryOptions.length : 0) +
        displayResults.length +
        (showExternalUrlRow ? 1 : 0) +
        (isId ? 1 : 0)

      if ((e.key === "ArrowUp" || e.key === "ArrowDown") && inputEmpty && !isInputFocused && input) {
        console.log("[Search History] Input not focused but empty - refocusing for history navigation")
        input.focus()
        isInputFocused = true
      }

      if (e.key === "ArrowUp" || e.key === "ArrowDown") {
        console.log("[Search History Debug]", {
          key: e.key,
          isInputFocused,
          inputEmpty,
          cursorAtStart,
          searchHistoryLength: searchHistory.length,
          historyIndex,
          currentInput,
          search,
          totalItems,
          inputValue: input?.value,
          activeElement: document.activeElement?.tagName,
          activeElementClass: (document.activeElement as HTMLElement)?.className,
        })
      }

      if (isInputFocused) {
        if (e.key === "ArrowUp" && searchHistory.length > 0 && (inputEmpty || cursorAtStart)) {
          console.log("[Search History] Navigating UP", {
            currentIndex: historyIndex,
            willShowItem:
              searchHistory[historyIndex === -1 ? 0 : Math.min(historyIndex + 1, searchHistory.length - 1)],
          })
          e.preventDefault()

          if (historyIndex === -1) {
            setCurrentInput(search)
          }

          const newIndex =
            historyIndex === -1 ? 0 : Math.min(historyIndex + 1, searchHistory.length - 1)
          setHistoryIndex(newIndex)
          setSearch(searchHistory[newIndex])
          setSelectedIndex(0)

          return
        } else if (e.key === "ArrowDown" && historyIndex > -1) {
          console.log("[Search History] Navigating DOWN", {
            currentIndex: historyIndex,
            willExit: historyIndex === 0,
          })
          e.preventDefault()

          if (historyIndex === 0) {
            setHistoryIndex(-1)
            setSearch(currentInput)
          } else {
            const newIndex = historyIndex - 1
            setHistoryIndex(newIndex)
            setSearch(searchHistory[newIndex])
          }
          setSelectedIndex(0)
          return
        }
      }

      if (e.key === "Tab" && isInputFocused && trimmedSearch.length > 0) {
        e.preventDefault()
        if (e.shiftKey) {
          setCurrentChatTarget("current")
          openChatWithPrompt(trimmedSearch, { newThread: false })
        } else {
          openChatWithPrompt(trimmedSearch, { newThread: currentChatTarget === "new" })
        }
        setOpen(false)
        return
      } else if (e.key === "Escape" && parsedQuery.mode !== "default") {
        e.preventDefault()
        e.stopPropagation()
        setSearch(parsedQuery.terms)
        setSelectedIndex(0)
        return
      } else if (e.key === "ArrowDown" && (!isInputFocused || !inputEmpty)) {
        e.preventDefault()
        setSelectedIndex((i) => (i + 1) % totalItems)
      } else if (e.key === "ArrowUp" && (!isInputFocused || !inputEmpty) && historyIndex === -1) {
        e.preventDefault()
        setSelectedIndex((i) => (i - 1 + totalItems) % totalItems)
      } else if (e.key === "Enter") {
        e.preventDefault()
        let currentIndex = 0

        if (showQueryOptions) {
          if (selectedIndex < queryOptions.length) {
            const option = queryOptions[selectedIndex]
            setSearch(option.label)
            setSelectedIndex(0)
            return
          }
          currentIndex += queryOptions.length
        }

        if (showExternalUrlRow) {
          if (selectedIndex === currentIndex) {
            handleSpecialSearchRef.current()
            return
          }
          currentIndex++
        }

        if (isId && !isUrl) {
          if (selectedIndex === currentIndex) {
            handleSpecialSearchRef.current()
            return
          }
          currentIndex++
        }

        const itemIndex = selectedIndex - currentIndex
        if (itemIndex >= 0 && itemIndex < displayResults.length) {
          handleSelectRef.current(displayResults[itemIndex])
        }
      }
    }

    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [
    open,
    selectedIndex,
    displayResults,
    search,
    trimmedSearch,
    isUrl,
    isMaaterDocUrl,
    isDriveUrl,
    isId,
    showQueryOptions,
    searchHistory,
    historyIndex,
    currentInput,
    parsedQuery,
    currentChatTarget,
    inputRef,
    setSelectedIndex,
    setSearch,
    setHistoryIndex,
    setCurrentInput,
    setCurrentChatTarget,
    setOpen,
    openChatWithPrompt,
    handleSelectRef,
    handleSpecialSearchRef,
  ])
}
